/**
 * Advanced Search Adapter for Facilities
 * Transforms field registry to Advanced Search field configurations
 */

import { FieldConfig } from '../../../components/TableSearch/types';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { FACILITIES_FIELDS_REGISTRY } from '../fields';
import { Facility } from '../../../services/api/domains/facilities/types';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Transform a field definition to search field config
 */
function toSearchFieldConfig(
  field: BaseFieldDefinition<Facility>
): FieldConfig | null {
  // Skip non-searchable fields
  if (!field.isSearchable) {
    return null;
  }

  // Special case: manufacturer_name should use manufacturer_id's relationConfig for async select
  if (field.key === 'manufacturer_name') {
    const manufacturerIdField = FACILITIES_FIELDS_REGISTRY.find(
      f => f.key === 'manufacturer_id'
    );
    if (manufacturerIdField?.isRelation && manufacturerIdField.relationConfig) {
      const contextConfig = getContextAwareConfigFromRegistry(
        FACILITIES_FIELDS_REGISTRY,
        'manufacturer_id',
        'search'
      );

      if (contextConfig) {
        const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

        return {
          key: field.key,
          label: field.label,
          dataType: 'selection',
          apiEndpoint: asyncConfig.apiEndpoint,
          searchField: asyncConfig.searchField,
          valueField: asyncConfig.valueField,
          labelField: asyncConfig.labelField,
        };
      }
    }
  }

  // Handle relational fields - use async select for API-based relations
  if (field.isRelation && field.relationConfig) {
    // Get context-aware config for search (uses name field)
    const contextConfig = getContextAwareConfigFromRegistry(
      FACILITIES_FIELDS_REGISTRY,
      field.key as string,
      'search'
    );

    if (contextConfig) {
      const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

      return {
        key: field.key,
        label: field.label,
        dataType: 'selection',
        apiEndpoint: asyncConfig.apiEndpoint,
        searchField: asyncConfig.searchField,
        valueField: asyncConfig.valueField,
        labelField: asyncConfig.labelField,
      };
    }
  }

  const config: FieldConfig = {
    key: field.key,
    label: field.label,
    dataType: field.dataType as 'string' | 'number' | 'date' | 'selection',
  };

  // Add selection options if available
  if (field.selectionOptions && field.selectionOptions.length > 0) {
    config.options = field.selectionOptions;
  }

  return config;
}

/**
 * Transform field registry to advanced search configuration
 */
export function toAdvancedSearchConfig(): FieldConfig[] {
  const searchFields = FACILITIES_FIELDS_REGISTRY.filter(
    f => f.isSearchable
  ).map(toSearchFieldConfig);

  // Filter out null values and return
  return searchFields.filter(
    (config): config is FieldConfig => config !== null
  );
}

/**
 * Get list of searchable field keys for validation
 */
export function getSearchableFieldKeys(): string[] {
  return FACILITIES_FIELDS_REGISTRY.filter(f => f.isSearchable).map(f => f.key);
}
